import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { collection, getDocs, doc, Timestamp, updateDoc, OrderByDirection, getDoc, orderBy, where, startAfter, limit, query } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { Pagination } from '../types/common';
import { ShopifyImage, ShopifyMedia, ShopifyMediaGeneration, ShopifyMediaPreview } from '../types/shopify';

export type { ShopifyImage, ShopifyMedia, ShopifyMediaGeneration, ShopifyMediaPreview, Pagination };


interface ShopifyMediaState {
    shopifyMedia: ShopifyMedia[];
    selectedShopifyMedia: ShopifyMedia | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
    // pagination
    pagination: Pagination;
    activeFilters: Record<string, any>;
    activeSorter: { field: string; order: OrderByDirection } | null;
}

const initialState: ShopifyMediaState = {
    shopifyMedia: [],
    selectedShopifyMedia: null,
    status: 'idle',
    error: null,
    pagination: {
        page: 1,
        pageSize: 20,
        lastVisibleDoc: null,
        hasMore: true
    },
    activeFilters: {},
    activeSorter: null,
};


export const updateShopifyMediaGeneration = createAsyncThunk(
    'shopifyMedia/updateShopifyMediaGeneration',
    async (
        args: { id: string; shopId: string; generation: Partial<ShopifyMediaGeneration> },
        { rejectWithValue }
    ) => {
        try {
            const { id, shopId, generation } = args;
            const timestamp = Timestamp.now();

            // Convert to dot notation for Firestore
            const generationUpdates: Record<string, any> = {};
            for (const [key, value] of Object.entries(generation)) {
                generationUpdates[`generation.${key}`] = value;
            }

            await updateDoc(doc(db, 'companies', shopId, 'shopifyMedia', id), {
                ...generationUpdates,
                updatedAt: timestamp,
            });

            return { id, ...generationUpdates, updatedAt: timestamp };
        } catch (error) {
            if (error instanceof Error) {
                return rejectWithValue(error.message);
            } else {
                return rejectWithValue('An unknown error occurred');
            }
        }
    }
);

export const updateShopifyMedia = createAsyncThunk(
    'shopifyMedia/updateShopifyMedia',
    async (updateShopifyMedia: Partial<ShopifyMedia> & { id: string, shopId: string }, { rejectWithValue }) => {
        try {
            const { id, shopId, ...updates } = updateShopifyMedia;
            const timestamp = Timestamp.now();

            // Update the document in Firestore
            await updateDoc(doc(db, 'companies', shopId, 'shopifyMedia', id), {
                ...updates,
                updatedAt: timestamp,  // Automatically update the updatedAt timestamp
            });

            return { id, ...updates, updatedAt: timestamp };
        } catch (error) {
            if (error instanceof Error) {
                return rejectWithValue(error.message);
            } else {
                return rejectWithValue('An unknown error occurred');
            }
        }
    }
);

export const refetchShopifyMedia = createAsyncThunk(
    'shopifyMedia/refetchShopifyMedia',
    async ({ shopifyShopId, mediaId }: { shopifyShopId: string; mediaId: string }, { rejectWithValue }) => {
        try {
            const docRef = doc(db, 'companies', shopifyShopId, 'shopifyMedia', mediaId);
            const docSnapshot = await getDoc(docRef);

            if (!docSnapshot.exists()) {
                throw new Error(`Media with ID ${mediaId} not found`);
            }

            return {
                id: docSnapshot.id,
                ...docSnapshot.data(),
            } as ShopifyMedia;
        } catch (error) {
            if (error instanceof Error) {
                return rejectWithValue(error.message);
            } else {
                return rejectWithValue('An unknown error occurred');
            }
        }
    }
);


export const fetchShopifyMedia = createAsyncThunk(
    'shopifyMedia/fetchShopifyMedia',
    async (
        { shopifyShopId,
            pageSize,
            lastVisibleDoc,
            activeSorter,
            activeFilters,
            selectedShopifyProductId,
            refetch
        }: {
            shopifyShopId: string,
            pageSize: number,
            lastVisibleDoc: any | null,
            activeSorter?: { field: string; order: OrderByDirection } | null
            activeFilters: Record<string, any>
            selectedShopifyProductId: string | undefined
            refetch: boolean
        }, { rejectWithValue }
    ) => {
        try {
            const constraints = [];

            console.log("selectedShopifyProductId", selectedShopifyProductId)

            // TODO: Remove when we handle other media then images
            constraints.push(where('mediaContentType', '==', 'IMAGE'))

            if (activeSorter) {
                constraints.push(orderBy(activeSorter.field, activeSorter.order));
            }

            // Apply filters dynamically
            Object.entries(activeFilters).forEach(([key, value]) => {
                if (value !== null && value !== undefined) {
                    if (key === 'alt' && value.length === 1) {
                        if (value[0]) {
                            constraints.push(where('alt', '>', ''));
                        } else {
                            constraints.push(where('alt', '==', null));
                        }
                    } else if (key === 'productTitle') {
                        constraints.push(
                            where('productTitle', '>=', value[0]),
                            where('productTitle', '<=', value[0] + '\uf8ff')
                        );
                    } else {
                        constraints.push(where(key, '==', value[0]));
                    }
                }
            });

            if (selectedShopifyProductId) {
                constraints.push(where('shopifyProductId', '==', selectedShopifyProductId));
            }

            // Handle pagination based on direction
            if (lastVisibleDoc) {
                constraints.push(startAfter(lastVisibleDoc));
            }
            constraints.push(limit(pageSize));

            const queryRef = query(collection(db, 'companies', shopifyShopId, 'shopifyMedia'), ...constraints);

            const querySnapshot = await getDocs(queryRef);
            const docs = querySnapshot.docs;
            console.log("length", docs.length)

            const lastDoc = docs.length > 0 ? docs[querySnapshot.docs.length - 1] : null;

            return {
                shopifyMedia: querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                })) as ShopifyMedia[],
                lastVisibleDoc: lastDoc,
                hasMore: querySnapshot.docs.length === pageSize,
                activeFilters: activeFilters,
                activeSorter: activeSorter ? activeSorter : null,
                refetch: refetch
            };
        } catch (error: any) {
            console.error("Error in fetchShopifyMedia:", error);
            return rejectWithValue(error.message || 'An unknown error occurred');
        }
    }
);

const shopifyMediaSlice = createSlice({
    name: 'shopifyMedia',
    initialState,
    reducers: {
        setShopifyMediaPagination: (state, action: PayloadAction<Pagination>) => {
            state.pagination = action.payload;
        },
        resetShopifyMedia: (state) => {
            state.shopifyMedia = [];
            state.pagination = { page: 1, pageSize: 20, lastVisibleDoc: null, hasMore: true };
            state.status = 'idle';
            state.error = null;
            state.activeFilters = {};
            state.activeSorter = null
        },
        setShopifyMedia: (state, action: PayloadAction<ShopifyMedia[]>) => {
            state.shopifyMedia = action.payload;
        },
        setSelectedShopifyMedia: (state, action: PayloadAction<ShopifyMedia | null>) => {
            state.selectedShopifyMedia = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchShopifyMedia.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchShopifyMedia.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const filtersChanged = JSON.stringify(state.activeFilters) !== JSON.stringify(action.payload.activeFilters)
                const sorterChanged = JSON.stringify(state.activeSorter) !== JSON.stringify(action.payload.activeSorter)
                if (filtersChanged || sorterChanged || action.payload.refetch) {
                    state.shopifyMedia = action.payload.shopifyMedia;
                } else {
                    state.shopifyMedia = [...state.shopifyMedia, ...action.payload.shopifyMedia];
                }
                // state.shopifyProducts = [...state.shopifyProducts, ...action.payload.products];
                state.pagination.lastVisibleDoc = action.payload.hasMore ? action.payload.lastVisibleDoc : state.pagination.lastVisibleDoc;
                state.pagination.hasMore = action.payload.hasMore
                state.activeFilters = action.payload.activeFilters
                state.activeSorter = action.payload.activeSorter
            })
            .addCase(fetchShopifyMedia.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message || null;
            }).addCase(refetchShopifyMedia.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(refetchShopifyMedia.fulfilled, (state, action) => {
                const updatedMedia = action.payload;
                const index = state.shopifyMedia.findIndex((media) => media.id === updatedMedia.id);

                // Update the product in the state
                if (index !== -1) {
                    state.shopifyMedia[index] = updatedMedia;
                }

                state.status = 'succeeded';
            })
            .addCase(refetchShopifyMedia.rejected, (state, action) => {
                state.status = 'failed';
                state.error = `Failed to refetch media: ${action.error.message}`;
            })
            .addCase(updateShopifyMedia.pending, (state, action) => {
                const { id, ...updates } = action.meta.arg;
                const index = state.shopifyMedia.findIndex((media) => media.id === id);

                // Optimistically update the product in the state
                if (index !== -1) {
                    state.shopifyMedia[index] = {
                        ...state.shopifyMedia[index],
                        ...updates,
                    };
                }
            })
            .addCase(updateShopifyMedia.fulfilled, (state, action) => {
                const { id } = action.payload;
                const index = state.shopifyMedia.findIndex((media) => media.id === id);

                // Replace the product with the updated product from the server
                if (index !== -1) {
                    state.shopifyMedia[index] = {
                        ...state.shopifyMedia[index],
                        ...action.payload,
                    };
                }
            })
            .addCase(updateShopifyMedia.rejected, (state, action) => {
                const { id, ...updates } = action.meta.arg;
                const index = state.shopifyMedia.findIndex((media) => media.id === id);

                // Revert the optimistic update if the backend operation fails
                if (index !== -1) {
                    state.shopifyMedia[index] = {
                        ...state.shopifyMedia[index],
                        ...updates,
                        // status: 'failed to update', // Add a flag to indicate failure
                    };
                }

                state.error = `Failed to update media: ${action.error.message}`;
            }).addCase(updateShopifyMediaGeneration.pending, (state, action) => {
                const { id, ...updates } = action.meta.arg;
                const index = state.shopifyMedia.findIndex((media) => media.id === id);

                // Optimistically update the product in the state
                if (index !== -1) {
                    state.shopifyMedia[index] = {
                        ...state.shopifyMedia[index],
                        generation: {
                            ...state.shopifyMedia[index].generation,
                            ...updates.generation
                        },
                    };
                }
            })
            .addCase(updateShopifyMediaGeneration.fulfilled, (state, action) => {
                const { id } = action.payload;
                const index = state.shopifyMedia.findIndex((media) => media.id === id);

                // Replace the product with the updated product from the server
                if (index !== -1) {
                    state.shopifyMedia[index] = {
                        ...state.shopifyMedia[index],
                        ...action.payload,
                    };
                }
            })
            .addCase(updateShopifyMediaGeneration.rejected, (state, action) => {
                const { id, ...updates } = action.meta.arg;
                const index = state.shopifyMedia.findIndex((media) => media.id === id);

                // Revert the optimistic update if the backend operation fails
                if (index !== -1) {
                    state.shopifyMedia[index] = {
                        ...state.shopifyMedia[index],
                        generation: {
                            ...state.shopifyMedia[index].generation,
                            ...updates.generation
                        },
                        // status: 'failed to update', // Add a flag to indicate failure
                    };
                }

                state.error = `Failed to update media: ${action.error.message}`;
            });

    },
});

export const { setShopifyMedia, setSelectedShopifyMedia, resetShopifyMedia, setShopifyMediaPagination } = shopifyMediaSlice.actions;
export default shopifyMediaSlice.reducer;
